/**
 * The flat marker glyphs, rasterised once into a strip the renderer samples.
 *
 * Transcribed from `packages/icons/src/svgSpriteSymbols/Arrow*.svg`, which is what the 2D views
 * draw and what the `Visualizations / Notation standards` story snapshots. Copied rather than
 * imported: a chart package that reached into the design system's icon sprite would take a
 * dependency on it for nine path strings, and these are notation that does not move. The source
 * file is named against each glyph so a change there can be found here.
 *
 * Rasterised rather than drawn analytically. Nine crow's-foot and tick-bar glyphs as signed
 * distance fields is nine chances to draw notation that is nearly right, and *nearly* is the one
 * thing a standard cannot be - a reader who knows crow's foot knows what it looks like.
 *
 * Drawn in pure black and white rather than in the sprite's `#1A222E`: the shader reads the
 * luminance as the mix between the theme's ink and its surface, so a glyph comes out dark-on-light
 * or light-on-dark without a second atlas. Transparent stays transparent.
 */

import {
  NETWORK_MARKER_GLYPHS,
  type NetworkMarkerGlyph,
} from '../networkMarkers';

/** The width of the viewBox every sprite marker is drawn in; its height is half that. */
const SOURCE_WIDTH = 28;

/** One cell, at the same 2:1 shape as the source. */
const MARKER_CELL_WIDTH = 64;
const MARKER_CELL_HEIGHT = 32;

export const MARKER_ATLAS_WIDTH =
  MARKER_CELL_WIDTH * NETWORK_MARKER_GLYPHS.length;
export const MARKER_ATLAS_HEIGHT = MARKER_CELL_HEIGHT;

type Stroke = {
  d: string;
  /** Source stroke width; the sprite draws every stroked glyph at 2. */
  width: number;
};

type Glyph = {
  /**
   * Where the reference's own vertex sits in source coordinates, the sprite's `refX`.
   *
   * The glyph is laid out so this lands on the cell's right edge, which is where the renderer
   * anchors it: a marker sits *at* the end of its reference with its body behind it.
   */
  vertexX: number;
  fills?: string[];
  strokes?: Stroke[];
  /** Hollow discs, as `zeroOrOne` and `zeroOrMany` carry: white fill, dark stroke. */
  circles?: { x: number; y: number; r: number }[];
};

/** Source: `ArrowDefault.svg`, `ArrowTop.svg`, `ArrowBottom.svg`, … */
const GLYPHS: Record<NetworkMarkerGlyph, Glyph> = {
  slash: {
    vertexX: 17,
    fills: [
      'M13.7809 2.6247C14.1259 2.19343 14.056 1.56414 13.6247 1.21913C13.1934 0.874122 12.5641 0.944043 12.2191 1.3753L13.7809 2.6247ZM4.21913 11.3753C3.87412 11.8066 3.94404 12.4359 4.3753 12.7809C4.80657 13.1259 5.43586 13.056 5.78087 12.6247L4.21913 11.3753ZM12.2191 1.3753L4.21913 11.3753L5.78087 12.6247L13.7809 2.6247L12.2191 1.3753Z',
    ],
  },
  chevron: { vertexX: 27, strokes: [{ d: 'M18 2L27 7L18 12', width: 2 }] },
  halfTop: { vertexX: 27, strokes: [{ d: 'M18 2L27 7', width: 2 }] },
  halfBottom: { vertexX: 27, strokes: [{ d: 'M27 7L18 12', width: 2 }] },
  one: {
    vertexX: 27,
    fills: [
      'M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2H20ZM18 12C18 12.5523 18.4477 13 19 13C19.5523 13 20 12.5523 20 12H18ZM18 2V12H20V2H18Z',
    ],
  },
  oneMandatory: {
    vertexX: 27,
    fills: [
      'M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2H20ZM18 12C18 12.5523 18.4477 13 19 13C19.5523 13 20 12.5523 20 12H18ZM18 2V12H20V2H18Z',
      'M14 2C14 1.44772 13.5523 1 13 1C12.4477 1 12 1.44772 12 2H14ZM12 12C12 12.5523 12.4477 13 13 13C13.5523 13 14 12.5523 14 12H12ZM12 2V12H14V2H12Z',
    ],
  },
  zeroOrOne: {
    vertexX: 27,
    fills: [
      'M27 2C27 1.44772 26.5523 1 26 1C25.4477 1 25 1.44772 25 2H27ZM25 12C25 12.5523 25.4477 13 26 13C26.5523 13 27 12.5523 27 12H25ZM25 2V12H27V2H25Z',
    ],
    circles: [{ x: 16, y: 7, r: 5 }],
  },
  many: {
    vertexX: 27,
    strokes: [{ d: 'M18 7L27 2M18 7L27 12M18 7H27', width: 2 }],
  },
  oneOrMany: {
    vertexX: 27,
    strokes: [{ d: 'M18 7L27 2M18 7L27 12M18 7H27', width: 2 }],
    fills: [
      'M15 2C15 1.44772 14.5523 1 14 1C13.4477 1 13 1.44772 13 2H15ZM13 12C13 12.5523 13.4477 13 14 13C14.5523 13 15 12.5523 15 12H13ZM13 2V12H15V2H13Z',
    ],
  },
  zeroOrMany: {
    vertexX: 27,
    strokes: [{ d: 'M18 7L27 2M18 7L27 12M18 7H27', width: 2 }],
    circles: [{ x: 9, y: 7, r: 5 }],
  },
};

const INK = '#000000';
const HOLLOW = '#ffffff';

const drawGlyph = (
  context: OffscreenCanvasRenderingContext2D,
  glyph: Glyph
): void => {
  const scale = MARKER_CELL_WIDTH / SOURCE_WIDTH;
  context.save();
  context.scale(scale, scale);
  /* Shifted so the sprite's own vertex lands on the cell's right edge. */
  context.translate(SOURCE_WIDTH - glyph.vertexX, 0);

  context.fillStyle = INK;
  context.strokeStyle = INK;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  for (const d of glyph.fills ?? []) context.fill(new Path2D(d));
  for (const { d, width } of glyph.strokes ?? []) {
    context.lineWidth = width;
    context.stroke(new Path2D(d));
  }
  for (const { x, y, r } of glyph.circles ?? []) {
    const disc = new Path2D();
    disc.arc(x, y, r, 0, Math.PI * 2);
    context.fillStyle = HOLLOW;
    context.fill(disc);
    context.lineWidth = 2;
    context.stroke(disc);
    context.fillStyle = INK;
  }
  context.restore();
};

/**
 * Every glyph in one strip, left to right in `NETWORK_MARKER_GLYPHS` order.
 *
 * `null` where the environment cannot rasterise one - a test runner, an old browser - which the
 * caller reads as "draw no planar markers" rather than as a failure. A reference still carries
 * its direction in the gradient and, where its type declares one, a solid marker.
 */
export const drawNetworkMarkerAtlas = (): OffscreenCanvas | null => {
  if (typeof OffscreenCanvas === 'undefined' || typeof Path2D === 'undefined') {
    return null;
  }

  const canvas = new OffscreenCanvas(MARKER_ATLAS_WIDTH, MARKER_ATLAS_HEIGHT);
  const context = canvas.getContext('2d');
  /* Asked what it can do rather than whether it exists. A test environment stubs `OffscreenCanvas`
     and hands back a context that draws nothing, which passed a null check and then threw on the
     first call - taking the whole view down on mount rather than quietly going without. */
  if (!context || typeof context.fill !== 'function') return null;

  NETWORK_MARKER_GLYPHS.forEach((name, index) => {
    context.save();
    context.translate(index * MARKER_CELL_WIDTH, 0);
    drawGlyph(context, GLYPHS[name]);
    context.restore();
  });

  return canvas;
};

/** Where a glyph's cell starts, as a fraction of the strip's width. */
export const toMarkerCellOffset = (glyph: NetworkMarkerGlyph): number =>
  NETWORK_MARKER_GLYPHS.indexOf(glyph) / NETWORK_MARKER_GLYPHS.length;
